'use client'

import { useForm, SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Link from 'next/link'
import { formatPhone, formatPhoneToApi } from '@/utils/formatPhone'
import { useUserStore } from '@/store/user'
import { useRouter, useSearchParams } from 'next/navigation'
import { AuthApi } from '@/api/auth'
import { useEffect, useRef } from 'react'
import { formatLettersFirstUpper } from '@/utils/formatInput'

const signupSchema = z.object({
    last_name: z.string().min(1, 'Фамилия обязательна'),
    first_name: z.string().min(1, 'Имя обязательно'),
    middle_name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string(),
    consentDataProcessing: z.boolean().refine((val) => val === true, {
        message: 'Необходимо дать согласие на обработку персональных данных',
    }),
    consentTerms: z.boolean().refine((val) => val === true, {
        message: 'Необходимо подтвердить ознакомление с пользовательским соглашением',
    }),
    consentMarketing: z.boolean().optional(),
})

type SignupFormData = z.infer<typeof signupSchema>

const inputClass =
    'px-3 py-2 border border-border rounded-md text-[0.95rem] text-[#333] h-10 bg-white transition-[border-color,box-shadow] duration-200 outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/10 placeholder-gray-400 w-full'
const labelClass = 'text-sm font-medium text-foreground'
const errorClass = 'block text-xs text-red-600 mt-1'

export default function AuthPage() {
    const { phone, error, setError, setValid, setPhone } = useUserStore()
    const router = useRouter()
    const phoneClear = useRef<string>('')
    const searchParams = useSearchParams()

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isValid },
    } = useForm<SignupFormData>({
        resolver: zodResolver(signupSchema),
        mode: 'onChange',
        defaultValues: {
            email: undefined,
        },
    })

    const firstName = watch('first_name')
    const lastName = watch('last_name')
    const middleName = watch('middle_name')

    useEffect(() => {
        if (error) setError('')
    }, [error])

    const onSubmit: SubmitHandler<SignupFormData> = (data) => {
        const { consentDataProcessing, consentTerms, consentMarketing, ...real_data } = data
        phoneClear.current = formatPhoneToApi(real_data.phone)

        setValid()

        AuthApi.RegisterTG({
            ...real_data,
            is_marketing_allowed: consentMarketing ?? false,
            is_personal_data_processing_accepted: consentDataProcessing,
            is_privacy_policy_accepted: consentTerms,
            phone: phoneClear.current,
            email: real_data.email === '' ? undefined : real_data.email,
        })
            .then((data) => {
                if ('error' in data && data.error) {
                    if (data.error === 'user already exists') {
                        setError('Пользователь с таким номером уже существует')
                    } else if (data.error === 'user not found') {
                        setError('Пользователь не найден')
                    } else setError(data.error)
                } else if ('tg_url' in data && phoneClear.current) {
                    if (data.tg_url) {
                        window.open(`https://${data.tg_url}`, '_blank')
                    }
                    const queryString = new URLSearchParams({
                        phone: phoneClear.current,
                    }).toString()
                    router.replace(`${process.env.LK_URL}/auth?${queryString}`)
                } else {
                    console.warn('Ошибка обработки номера')
                    console.warn({ data }, { phoneClear })
                }
            })
            .catch((reject) => {
                /* TODO: вспомнить что за условие */
                if ('digest' in reject) return

                /* Если пользователь случайно регистрируется повторно и уже существует */
                if (reject.response?.data && 'error' in reject.response?.data && reject.response?.data.error === 'user already exists') {
                    setError('Такой пользователь уже зарегистрирован. Пожалуйста, войдите в аккаунт.')

                    const redirectParams = new URLSearchParams(searchParams.toString())
                    redirectParams.set('phone', formatPhoneToApi(data.phone))
                    redirectParams.set('reason', 'Такой пользователь уже существует, войдите')

                    if ('is_personal_data_processing_accepted' in data && 'is_privacy_policy_accepted' in data) {
                        redirectParams.set('is_personal_data_processing_accepted', String(data.is_personal_data_processing_accepted))
                        redirectParams.set('is_privacy_policy_accepted', String(data.is_privacy_policy_accepted))
                    }

                    router.push(`${process.env.LK_URL}/auth?${redirectParams.toString()}`)
                    return
                }

                /* Просто некая ошибка с бека */
                if (reject.response?.data && 'error' in reject.response?.data) {
                    setPhone(phone)
                    router.push('/auth/signin')
                    return
                } else {
                    /* Мистика */
                    setError(JSON.stringify(reject.response?.data) || 'неизвестная ошибка')
                }
            })
    }

    return (
        <div className="max-w-md mx-auto w-full">
            <div className="mb-8">
                <h2 className="text-2xl font-bold leading-8 text-foreground mb-2">Регистрация</h2>
                <p className="text-[rgb(115,115,115)]">Создайте аккаунт для подачи заявки на банкротство</p>
            </div>

            {/* eslint-disable-next-line react-hooks/refs */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
                {/* Фамилия и Имя */}
                <div className="grid grid-cols-2 max-[480px]:grid-cols-1 gap-4">
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="lastName" className={labelClass}>
                            Фамилия *
                        </label>
                        <input
                            onChange={(e) => {
                                const name = formatLettersFirstUpper(e.target.value)
                                setValue('last_name', name)
                            }}
                            type="text"
                            id="lastName"
                            value={lastName}
                            placeholder="Иванов"
                            className={`${inputClass} ${errors.last_name && 'border-red-600 focus:ring-red-600/10'}`}
                        />
                        {errors.last_name && <span className={errorClass}>{errors.last_name.message}</span>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label htmlFor="firstName" className={labelClass}>
                            Имя *
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            onChange={(e) => {
                                const name = formatLettersFirstUpper(e.target.value)
                                setValue('first_name', name)
                            }}
                            value={firstName}
                            placeholder="Иван"
                            className={`${inputClass} ${errors.first_name ? 'border-red-600 focus:ring-red-600/10' : ''}`}
                        />
                        {errors.first_name && <span className={errorClass}>{errors.first_name.message}</span>}
                    </div>
                </div>

                {/* Отчество */}
                <div className="flex flex-col gap-1.5">
                    <label htmlFor="middleName" className={labelClass}>
                        Отчество
                    </label>
                    <input
                        type="text"
                        id="middleName"
                        placeholder="Иванович"
                        className={inputClass}
                        onChange={(e) => {
                            const name = formatLettersFirstUpper(e.target.value)
                            setValue('middle_name', name)
                        }}
                        value={middleName}
                    />
                </div>

                {/* Электронная почта */}
                <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className={labelClass}>
                        Электронная почта
                    </label>
                    <input
                        type="email"
                        id="email"
                        {...register('email')}
                        placeholder="example@mail.ru"
                        className={`${inputClass} ${errors.email ? 'border-red-600 focus:ring-red-600/10' : ''}`}
                    />
                    {errors.email && <span className={errorClass}>{errors.email.message}</span>}
                </div>

                {/* Телефон */}
                <div className="flex flex-col gap-1.5">
                    <label htmlFor="phone" className={labelClass}>
                        Телефон *
                    </label>
                    <input
                        type="tel"
                        defaultValue={phone}
                        id="phone"
                        {...register('phone', {
                            onChange: (e) => {
                                const formatted = formatPhone(e.target.value)
                                e.target.value = formatted
                            },
                        })}
                        placeholder="+7 (___) ___-__-__"
                        className={`${inputClass} ${errors.phone ? 'border-red-600 focus:ring-red-600/10' : ''}`}
                    />
                    {errors.phone && <span className={errorClass}>{errors.phone.message}</span>}
                </div>

                {/* Чекбоксы согласий */}
                <div className="flex flex-col gap-3 mt-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" {...register('consentDataProcessing')} className="w-4 h-4 mt-0.5 accent-violet-500 cursor-pointer shrink-0" />
                        <span className="text-[0.8125rem] text-muted-foreground leading-[1.4]">
                            Даю согласие на{' '}
                            <Link href="/privacy" target="_blank" className="text-purple-800 underline hover:no-underline">
                                обработку персональных данных
                            </Link>{' '}
                            *
                        </span>
                    </label>
                    {errors.consentDataProcessing && <span className={errorClass}>{errors.consentDataProcessing.message}</span>}

                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" {...register('consentTerms')} className="w-4 h-4 mt-0.5 accent-violet-500 cursor-pointer shrink-0" />
                        <span className="text-[0.8125rem] text-muted-foreground leading-[1.4]">
                            Подтверждаю ознакомление с{' '}
                            <Link href="/agreement" target="_blank" className="text-purple-800 underline hover:no-underline">
                                пользовательским соглашением
                            </Link>{' '}
                            *
                        </span>
                    </label>
                    {errors.consentTerms && <span className={errorClass}>{errors.consentTerms.message}</span>}

                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" {...register('consentMarketing')} className="w-4 h-4 mt-0.5 accent-violet-500 cursor-pointer shrink-0" />
                        <span className="text-[0.8125rem] text-muted-foreground leading-[1.4]">Согласен на получение информационных и рекламных материалов</span>
                    </label>
                </div>

                {/* Кнопка отправки */}
                <button
                    type="submit"
                    className={`w-full py-3.5 px-6 bg-[rgb(90,0,224)] text-white border-none rounded-lg text-base font-medium  transition-[opacity,transform] duration-200 mt-2 hover:opacity-90 active:scale-[0.98] ${!isValid ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    Зарегистрироваться
                </button>

                {error && <p className="block text-xs text-red-600 mt-1 text-center">{error}</p>}

                <p className="text-center text-sm text-[rgb(115,115,115)] mt-6">
                    Уже есть аккаунт?{' '}
                    <Link href="/auth/signin" onClick={() => setError('')} className="text-[rgb(90,0,224)] font-medium hover:underline">
                        Войдите
                    </Link>
                </p>
            </form>
        </div>
    )
}
